import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "../lib/api";
import { dateOnly, money } from "../lib/format";
import i18n from "../lib/i18n";
import { useCompanySettings } from "../lib/useCompanySettings";

type InvoiceStatus = "draft" | "sent" | "paid" | "void";

interface InvoiceLineItemRow {
  id: string;
  description: string;
  quantity: string;
  unitPrice: string;
  lineTotal: string;
}

interface InvoiceDetailData {
  id: string;
  invoiceNumber: string;
  status: InvoiceStatus;
  clientName: string;
  clientEmail: string | null;
  clientAddress: string | null;
  currency: string;
  issueDate: string;
  dueDate: string | null;
  subtotal: string;
  taxPercent: string;
  taxTotal: string;
  total: string;
  notes: string | null;
  lineItems: InvoiceLineItemRow[];
}

const STATUS_VARIANT: Record<InvoiceStatus, "secondary" | "info" | "success" | "destructive"> = {
  draft: "secondary",
  sent: "info",
  paid: "success",
  void: "destructive",
};

export default function InvoiceDetail() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const params = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { settings } = useCompanySettings();

  const invoiceQ = useQuery({
    queryKey: ["invoice", params.id],
    queryFn: () => api<InvoiceDetailData>(`/api/v1/invoices/${params.id}`),
    enabled: Boolean(params.id),
  });

  const statusMutation = useMutation({
    mutationFn: (status: Exclude<InvoiceStatus, "draft">) =>
      api(`/api/v1/invoices/${params.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["invoice", params.id] });
      void qc.invalidateQueries({ queryKey: ["invoices"] });
    },
  });

  if (invoiceQ.isLoading) {
    return <p className="text-muted-foreground">{t("common:loading")}</p>;
  }
  if (!invoiceQ.data) {
    return <p className="text-muted-foreground">{t("invoices:detail.notFound")}</p>;
  }

  const inv = invoiceQ.data;

  return (
    <>
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link href="/invoices">
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {t("invoices:detail.back")}
          </Link>
        </Button>
        <div className="flex flex-wrap gap-2">
          {inv.status === "draft" && (
            <Button
              variant="outline"
              size="sm"
              disabled={statusMutation.isPending}
              onClick={() => statusMutation.mutate("sent")}
            >
              {t("invoices:detail.markSent")}
            </Button>
          )}
          {(inv.status === "draft" || inv.status === "sent") && (
            <Button
              variant="outline"
              size="sm"
              disabled={statusMutation.isPending}
              onClick={() => statusMutation.mutate("paid")}
            >
              {t("invoices:detail.markPaid")}
            </Button>
          )}
          {inv.status !== "void" && inv.status !== "paid" && (
            <Button
              variant="outline"
              size="sm"
              disabled={statusMutation.isPending}
              onClick={() => statusMutation.mutate("void")}
            >
              {t("invoices:detail.void")}
            </Button>
          )}
          <Button asChild size="sm">
            <a href={`/api/v1/invoices/${params.id}/pdf`} target="_blank" rel="noopener noreferrer">
              <Download className="h-4 w-4" aria-hidden />
              {t("invoices:detail.downloadPdf")}
            </a>
          </Button>
        </div>
      </div>

      {statusMutation.isError && (
        <p className="no-print mb-4 text-sm text-destructive">
          {t("invoices:detail.statusUpdateFailed")}
        </p>
      )}

      <Card className="mx-auto max-w-3xl">
        <CardContent className="p-8">
          <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl">{t("invoices:detail.invoiceNumber")}</h1>
              <p className="font-mono text-lg">{inv.invoiceNumber}</p>
              <Badge variant={STATUS_VARIANT[inv.status]} className="mt-2">
                {t(`invoices:status.${inv.status}`)}
              </Badge>
            </div>
            {settings && (
              <div className="text-right text-sm text-muted-foreground">
                {settings.logoDataUrl && (
                  <img
                    src={settings.logoDataUrl}
                    alt=""
                    className="ml-auto mb-2 h-10 w-auto max-w-[10rem] object-contain"
                  />
                )}
                <p className="font-medium text-foreground">
                  {settings.companyName || t("common:appName")}
                </p>
                {settings.addressLine1 && <p>{settings.addressLine1}</p>}
                {settings.addressLine2 && <p>{settings.addressLine2}</p>}
                {(settings.city || settings.state || settings.postalCode) && (
                  <p>
                    {[settings.city, settings.state, settings.postalCode]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}
                {settings.email && <p>{settings.email}</p>}
                {settings.phone && <p>{settings.phone}</p>}
              </div>
            )}
          </div>

          <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <p className="text-xs text-muted-foreground">{t("invoices:detail.billTo")}</p>
              <p className="font-medium">{inv.clientName}</p>
              {inv.clientEmail && (
                <p className="text-sm text-muted-foreground">{inv.clientEmail}</p>
              )}
              {inv.clientAddress && (
                <p className="whitespace-pre-line text-sm text-muted-foreground">
                  {inv.clientAddress}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("invoices:detail.issueDate")}</p>
              <p>{dateOnly(inv.issueDate, locale)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">{t("invoices:detail.dueDate")}</p>
              <p>{inv.dueDate ? dateOnly(inv.dueDate, locale) : "—"}</p>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("invoices:form.description")}</TableHead>
                <TableHead className="text-right">{t("invoices:form.quantity")}</TableHead>
                <TableHead className="text-right">{t("invoices:form.unitPrice")}</TableHead>
                <TableHead className="text-right">{t("invoices:form.lineTotal")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inv.lineItems.map((li) => (
                <TableRow key={li.id}>
                  <TableCell>{li.description}</TableCell>
                  <TableCell className="text-right tabular-nums">{li.quantity}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(li.unitPrice, inv.currency, locale)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {money(li.lineTotal, inv.currency, locale)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-4 flex justify-end">
            <div className="w-full max-w-xs space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("invoices:form.subtotal")}</span>
                <span className="tabular-nums">{money(inv.subtotal, inv.currency, locale)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  {t("invoices:form.tax")} ({Number(inv.taxPercent)}%)
                </span>
                <span className="tabular-nums">{money(inv.taxTotal, inv.currency, locale)}</span>
              </div>
              <div className="flex justify-between border-t pt-1 text-base font-semibold">
                <span>{t("invoices:form.total")}</span>
                <span className="tabular-nums">{money(inv.total, inv.currency, locale)}</span>
              </div>
            </div>
          </div>

          {inv.notes && (
            <div className="mt-8 border-t pt-4">
              <p className="mb-1 text-xs text-muted-foreground">{t("invoices:detail.notes")}</p>
              <p className="whitespace-pre-line text-sm">{inv.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
